"use client";

import { AppGrid } from "@/components/apps/app-grid";
import { Separator } from "@/components/ui/separator";
import { useApps } from "@/hooks/use-app";
import { usePagination } from "@/hooks/use-pagination";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState, useCallback, useMemo, useEffect } from "react";

export default function AppStorePage() {
  const { pagination, limit, offset, setPageIndex } = usePagination({ 
    initialPageSize: 50 
  });
  
  const [filters, setFilters] = useState<{
    searchQuery?: string;
    category?: string;
    authType?: string;
    sortOrder?: string;
    configurationFilter?: string;
  }>({});

  // Fetch all apps once to get categories and auth types
  const { data: allAppsData } = useApps();
  
  // Extract unique categories and auth types from all apps
  const allCategories = useMemo(() => {
    if (!allAppsData) return [];
    return Array.from(new Set(allAppsData.flatMap((app) => app.categories)));
  }, [allAppsData]);

  const allAuthTypes = useMemo(() => {
    if (!allAppsData) return [];
    return Array.from(
      new Set(
        allAppsData.flatMap((app) => Object.keys(app.supported_security_schemes || {})),
      ),
    );
  }, [allAppsData]);

  // Build API params based on filters and pagination
  const apiParams = useMemo(() => {
    const params: {
      limit: number;
      offset: number;
      search_query?: string;
      categories?: string[];
      security_schemes?: string[];
      sort_by?: string;
      sort_order?: string;
    } = {
      limit,
      offset,
      sort_by: "display_name",
      sort_order: filters.sortOrder || "asc",
    };

    if (filters.searchQuery) {
      params.search_query = filters.searchQuery;
    }

    if (filters.category && filters.category !== "All Categories") {
      params.categories = [filters.category];
    }

    if (filters.authType && filters.authType !== "All Auth Types") {
      params.security_schemes = [filters.authType];
    }

    return params;
  }, [limit, offset, filters]);

  // Fetch apps with current filters and pagination
  const { data: apps, isPending, isError } = useApps(apiParams);

  // Reset to first page when filters change
  useEffect(() => {
    setPageIndex(0);
  }, [filters.searchQuery, filters.category, filters.authType, setPageIndex]);

  const handleFiltersChange = useCallback((newFilters: typeof filters) => {
    setFilters(newFilters);
  }, []);

  const handlePageChange = useCallback((page: number) => {
    setPageIndex(page);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [setPageIndex]);

  // Calculate total pages (we don't have total count from API, so we estimate)
  // If we get a full page, there might be more pages
  const totalPages = useMemo(() => {
    if (!apps) return 1;
    
    // If we got fewer apps than the page size, we're on the last page
    if (apps.length < limit) {
      return pagination.pageIndex + 1;
    }
    
    // Otherwise, we assume there's at least one more page
    // This is a limitation without a total count from the API
    return pagination.pageIndex + 2;
  }, [apps, limit, pagination.pageIndex]);

  const totalApps = useMemo(() => {
    if (!apps) return 0;
    // Estimate: current page * page size + current results
    return pagination.pageIndex * limit + apps.length;
  }, [apps, pagination.pageIndex, limit]);

  return (
    <div>
      <div className="m-4">
        <h1 className="text-2xl font-bold">App Store</h1>
        <p className="text-sm text-muted-foreground">
          Browse and connect with your favorite apps and tools.
        </p>
      </div>
      <Separator />

      <div className="m-4">
        {isPending ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="animate-spin h-10 w-10 text-muted-foreground" />
            Loading apps...
          </div>
        ) : isError ? (
          <div className="flex justify-center items-center py-16">
            <AlertCircle className="h-10 w-10 text-destructive" />
            Failed to load apps. Please try to refresh the page.
          </div>
        ) : (
          <AppGrid 
            apps={apps || []} 
            totalApps={totalApps}
            allCategories={allCategories}
            allAuthTypes={allAuthTypes}
            onFiltersChange={handleFiltersChange}
            onPageChange={handlePageChange}
            currentPage={pagination.pageIndex}
            pageSize={limit}
            totalPages={totalPages}
          />
        )}
      </div>
    </div>
  );
}
